<template>
  <div>
    <b-button
      v-if="!hideFlugstammdatenButton"
      class="ml-auto d-flex justify-content-center align-items-center p-2"
      @click="openFilteredFlugstammdatenView"
    >
      Gehe zu Flugstammdaten
    </b-button>
    <b-table
      style="border: 1px solid rgb(0, 0, 0, 0.1); border-radius: 5px; margin-top: 12px"
      :items="fvcData"
      :fields="flugverfuegbarkeitenFields"
      :sort-by="'startdatumHotelleistung'"
      :sort-desc="false"
      responsive
      head-variant="light"
    >
      <template v-slot:cell(actions)="row">
        <div class="d-flex" style="gap: 5px">
          <a
            class="kachel-badge kachel-badge--square gray text-decoration-none"
            :href="
              '/#/reisetermine/fvc/overview/?reise=' + row.item.reisetermin.reiseterminkuerzel.substring(0, 6)
            "
            target="_blank"
          >
            FVC
          </a>
          <a
            class="kachel-badge kachel-badge--square gray text-decoration-none"
            :href="'/#/reisetermine/' + row.item.reisetermin.id"
            target="_blank"
          >
            RT
          </a>
          <a
            class="kachel-badge kachel-badge--square gray text-decoration-none"
            :href="'/#/reisen/' + row.item.reisetermin.reise.reisekuerzel + '/reisestammdaten'"
            target="_blank"
          >
            R
          </a>
        </div>
      </template>
      <template v-slot:cell(reisetermin.reiseterminkuerzel)="data">
        <div style="width: 150px">
          {{ data.value }}
        </div>
      </template>
      <template v-slot:cell(status)="row">
        <span
          class="kachel-badge"
          :class="
            row.item.status === 'Verfuegbar'
              ? 'green'
              : row.item.status === 'NichtVerfuegbar'
              ? 'red'
              : 'yellow'
          "
        >
          {{ row.item.status === 'Verfuegbar' ? 'J' : row.item.status === 'NichtVerfuegbar' ? 'N' : 'RQ' }}
        </span>
      </template>
      <template v-slot:cell(reisezeitraum)="row">
        <div style="width: 150px">
          {{ formatDateWithoutHours(row.item.reisetermin.abreisedatum) }} -
          {{ formatDateWithoutHours(row.item.reisetermin.enddatum) }}
        </div>
      </template>
      <template v-slot:cell(pax)="row">
        <PaxWidget
          :paxObject="row.item.reisetermin.pax"
          :minPax="row.item.reisetermin.metadata.minPax"
          :maxPax="row.item.reisetermin.metadata.maxPax"
          :kalkPax="row.item.reisetermin.metadata.kalkPax"
          :forecastPax="row.item.reisetermin.metadata.forecastPax"
          :durchfuehrbareMinPax="row.item.reisetermin.metadata.durchfuehrbareMinPax"
        ></PaxWidget>
      </template>
      <template v-slot:cell(ziellandAbUndZielflughafen)="row">
        {{ row.item.reisetermin.metadata.ziellandZielflughaefen.map(fh => fh.flughafenIataCode).join(', ') }}
        /
        {{ row.item.reisetermin.metadata.ziellandAbflughaefen.map(fh => fh.flughafenIataCode).join(', ') }}
      </template>

      <template v-slot:cell(reisetermin.abgesagt)="row">
        <CustomIconChecked :value="row.item.reisetermin.abgesagt" />
      </template>
      <template v-slot:cell(reisetermin.garantiert)="row">
        <CustomIconChecked :value="row.item.reisetermin.garantiert" />
      </template>
      <template v-slot:cell(reisetermin.stopSale)="row">
        <CustomIconExclamation :value="row.item.reisetermin.stopSale" />
      </template>
      <template v-slot:cell(aufgaben)="row">
        <b-badge :id="'tooltip-target-flugverfuegbarkeit' + row.item.id">
          {{ row.item.aufgaben?.length }}
        </b-badge>
        <b-tooltip
          :target="'tooltip-target-flugverfuegbarkeit' + row.item.id"
          triggers="hover"
          custom-class="custom-tooltip"
          v-if="row.item.aufgaben?.length > 1"
        >
          <AufgabenWidget
            :openAufgabenId="openAufgabenId"
            class="mt-3"
            :ansicht="'aufgabe-details'"
            :aufgaben="row.item.aufgaben || []"
          ></AufgabenWidget>
        </b-tooltip>
      </template>
    </b-table>
  </div>
</template>
<script>
import PaxWidget from '@/components/produkte/reisetermine/pax-widget.vue';
import AufgabenWidget from '@/components/produkte/reisetermine/aufgaben-widget.vue';
import CustomIconChecked from '@/components/common/custom-icon-checked.vue';
import CustomIconExclamation from '@/components/common/custom-icon-exclamation.vue';
import { formatDateWithoutHours } from '@/core/common/helpers/utils';
export default {
  components: { PaxWidget, AufgabenWidget, CustomIconChecked, CustomIconExclamation },
  props: {
    fvcData: { type: Array, required: true },
    openAufgabenId: { type: Number, default: null },
    hideFlugstammdatenButton: { type: Boolean, default: false },
  },
  data() {
    return {
      flugverfuegbarkeitenFields: [
        {
          tdClass: 'align-middle',
          key: 'reisetermin.reiseterminkuerzel',
          label: 'Reiseterminkürzel',
        },
        { tdClass: 'align-middle', key: 'actions', label: 'Links' },
        {
          tdClass: 'align-middle',
          key: 'reisetermin.countryManagers',
          label: 'CM',
          formatter: value => value?.map(cm => cm.countryManagerKuerzel).join(', ') ?? '-',
        },
        { tdClass: 'align-middle', key: 'deutscherAbflughafenIataCode', label: 'Abflughafen' },
        {
          tdClass: 'align-middle',
          key: 'startdatumHotelleistung',
          label: 'Startdatum',
          formatter: value => {
            return this.formatDateWithoutHours(value);
          },
        },
        { tdClass: 'align-middle', key: 'status' },
        {
          tdClass: 'align-middle',
          key: 'aufpreis',
          formatter: value => (!isNaN(parseInt(value)) ? value + '€' : '-'),
        },
        {
          tdClass: 'align-middle',
          key: 'flugstammHinflug',
          label: 'Flugstamm Hin',
          thStyle: { whiteSpace: 'nowrap' },
        },
        { tdClass: 'align-middle', key: 'zimmertyp' },
        { tdClass: 'align-middle', key: 'pax' },
        {
          tdClass: 'align-middle',
          key: 'reisezeitraum',
        },
        {
          tdClass: 'align-middle',
          key: 'reisetermin.reise.prioritaet',
          label: 'Prio.',
          formatter: value => (value === 0 ? 'A' : value === 1 ? 'B' : value === 2 ? 'C' : 'D'),
        },
        {
          tdClass: 'align-middle',
          key: 'reisetermin.metadata.transferszenario',
          label: 'TS',
          formatter: value => value?.substring(0, 2),
        },
        {
          tdClass: 'align-middle',
          key: 'ziellandAbUndZielflughafen',
          label: 'Zielland Ziel-/Abfh.',
          thStyle: { whiteSpace: 'nowrap' },
        },
        {
          tdClass: 'align-middle',
          key: 'reisetermin.thLangstrecke',
          label: 'TH Wert',
          thStyle: { whiteSpace: 'nowrap' },
          formatter: value => (isNaN(parseInt(value)) ? '-' : value + '€'),
        },
        {
          tdClass: 'align-middle',
          key: 'abfragefehler.text',
          label: 'Abfragefehler',
        },
        {
          tdClass: 'align-middle',
          key: 'reisetermin.abgesagt',
          label: 'Abgesagt',
        },
        {
          tdClass: 'align-middle',
          key: 'reisetermin.garantiert',
          label: 'Garantiert',
        },
        {
          tdClass: 'align-middle',
          key: 'reisetermin.stopSale',
          label: 'Stop Sale',
          thStyle: { whiteSpace: 'nowrap' },
        },
        { tdClass: 'align-middle', key: 'aufgaben', label: 'Aufgaben' },
      ],
    };
  },
  methods: {
    formatDateWithoutHours,
    openFilteredFlugstammdatenView() {
      const reisekuerzel = [...new Set(this.fvcData.map(item => item.reisetermin.reise.reisekuerzel))];
      const years = this.fvcData.map(item => new Date(item.reisetermin.abreisedatum).getFullYear());
      const minYear = Math.min(...years);
      const maxYear = Math.max(...years);
      const abreisedatumVon = `${minYear}-01-01`;
      const abreisedatumBis = `${maxYear}-12-31`;
      window.open(
        `/#/flugstammdaten?reise=${reisekuerzel}&abreisedatumVon=${abreisedatumVon}&abreisedatumBis=${abreisedatumBis}`,
        '_blank'
      );
    },
  },
};
</script>
